/**
 * Recharts geometry helpers for Arabic.
 *
 * Recharts renders every tick, bar, label and reference line as SVG placed with absolute
 * coordinates and `text-anchor`, and both resolve against the inline base direction. Letting a
 * chart inherit `dir="rtl"` from `<html>` therefore does not mirror it — it flips the anchoring
 * underneath geometry that was computed for LTR, so axis labels drift over their own bars and get
 * clipped at the panel edge.
 *
 * The convention across the dashboards is: pin `.recharts-surface` to `direction: ltr` in CSS so
 * that geometry stays consistent, and mirror the layout deliberately through the props below.
 * Scope the CSS to the surface — `.recharts-tooltip-wrapper` is a sibling `div` that must keep the
 * page direction so its Arabic text reads right-to-left.
 */

/** Radius on the bar's growing end only, for `layout="vertical"` (horizontal) bars. */
export const mirrorHorizontalBarRadius = (
  radius: number,
  isRTL: boolean
): [number, number, number, number] =>
  isRTL ? [radius, 0, 0, radius] : [0, radius, radius, 0];

/** Recharts renders LTR internally; these mirror axis/label placement for Arabic. */
export const rtlChartProps = (isRTL: boolean) => ({
  /** Category (vertical-layout) axis flips to the right edge. */
  yAxisOrientation: isRTL ? ("right" as const) : ("left" as const),
  /** Second axis of a dual-axis chart, always opposite `yAxisOrientation`. */
  oppositeYAxisOrientation: isRTL ? ("left" as const) : ("right" as const),
  /** Value axes grow right-to-left. */
  axisReversed: isRTL,
  /** Bar value labels sit at the bar's growing end. */
  labelPosition: isRTL ? ("left" as const) : ("right" as const),
  /** Rounded corners belong on the bar's growing end. */
  horizontalBarRadius: mirrorHorizontalBarRadius(6, isRTL),
});

/** Left/right margins swap so the flipped axis keeps its gutter and labels are not clipped. */
export const mirrorChartMargin = <T extends { left?: number; right?: number }>(
  margin: T,
  isRTL: boolean
): T => (isRTL ? { ...margin, left: margin.right, right: margin.left } : margin);
